import { type ReactNode, useMemo } from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import { useTour, useTours } from "@tour-kit/react";
import {
  type FabMenuConfig,
  type FabMenuItem,
  type FabMenuSection,
  useFabMenuConfig,
} from "src/business-components/FabButton/FabButton.state";
import { trackTourIntentSelected } from "../onboarding.analytics";
import { useRegisteredTours } from "../tours/registeredTours.state";
import type { DefinityTourDef } from "../tours/types";

const TOUR_MENU_TAKE_A_TOUR = "Take a tour";
const TOUR_MENU_QUICK_HELP = "Quick help";

const TourFab = () => {
  const tours = useRegisteredTours();
  const { isAnyTourActive, isTourCompleted } = useTours();
  const { start } = useTour();

  const completionSignature = tours
    .map((t) => `${t.id}:${isTourCompleted(t.id)}`)
    .join(",");

  // biome-ignore lint/correctness/useExhaustiveDependencies: completionSignature is an intentional extra dep tracking isTourCompleted results (stable fn ref, changing return value)
  const config = useMemo((): FabMenuConfig => {
    if (tours.length === 0) {
      return { sections: [], hidden: isAnyTourActive };
    }

    const general = tours.find((t) => t.kind === "general");
    const intents = tours.filter((t) => t.kind === "intent");

    const toMenuItem = (
      tour: DefinityTourDef,
      icon: ReactNode,
      completed: boolean
    ): FabMenuItem => ({
      id: tour.id,
      label: tour.label,
      description: tour.description,
      icon,
      onClick: () => {
        trackTourIntentSelected(tour.id, tour.kind);
        start(tour.id);
      },
      endAdornment: completed ? (
        <CheckCircleIcon
          fontSize="small"
          className="shrink-0 text-success-light"
          aria-label="Completed"
        />
      ) : undefined,
    });

    const sections: FabMenuSection[] = [];

    if (general) {
      sections.push({
        id: "tours-general",
        header: TOUR_MENU_TAKE_A_TOUR,
        items: [
          toMenuItem(
            general,
            <MapOutlinedIcon fontSize="small" />,
            isTourCompleted(general.id)
          ),
        ],
      });
    }

    if (intents.length > 0) {
      sections.push({
        id: "tours-intents",
        header: TOUR_MENU_QUICK_HELP,
        items: intents.map((intent) =>
          toMenuItem(
            intent,
            <LightbulbOutlinedIcon fontSize="small" />,
            isTourCompleted(intent.id)
          )
        ),
      });
    }

    return { sections, hidden: isAnyTourActive };
  }, [tours, isAnyTourActive, completionSignature, start, isTourCompleted]);

  useFabMenuConfig(config);

  return null;
};

export default TourFab;
